import { Helmet } from 'react-helmet-async'
import { SITE } from '../lib/links'
import { useLocale, usePageKey } from '../i18n/LocaleContext'
import { ROUTES, alternatesFor } from '../i18n/routes'
import { LOCALE_META, type Locale, LOCALES } from '../i18n'

type Props = {
  title: string
  description: string
  path?: string
  canonical?: string
  image?: string
  jsonLd?: object | object[]
  /** URLs alternatives par langue (chemins relatifs). Si absent, pas de hreflang. */
  alternates?: Record<Locale, string>
}

const DEFAULT_OG_IMAGE = `${SITE.url}/og-default.png`

export default function SeoHead({
  title,
  description,
  path,
  canonical,
  image,
  jsonLd,
  alternates,
}: Props) {
  const locale = useLocale()
  const pageKey = usePageKey()
  // Pour une page statique connue, calcule les alternates hreflang automatiquement.
  const autoAlternates =
    pageKey && pageKey in ROUTES ? alternatesFor(pageKey) : undefined
  const effectiveAlternates = alternates ?? autoAlternates
  const url = canonical ?? (path ? `${SITE.url}${path}` : SITE.url)
  const ogImage = image ?? DEFAULT_OG_IMAGE

  const schemas: object[] = Array.isArray(jsonLd) ? jsonLd : jsonLd ? [jsonLd] : []

  return (
    <Helmet>
      <html lang={LOCALE_META[locale].htmlLang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      <link rel="canonical" href={url} />

      {effectiveAlternates &&
        LOCALES.map((l) => (
          <link
            key={l}
            rel="alternate"
            hrefLang={LOCALE_META[l].htmlLang}
            href={`${SITE.url}${effectiveAlternates[l]}`}
          />
        ))}
      {effectiveAlternates && (
        <link rel="alternate" hrefLang="x-default" href={`${SITE.url}${effectiveAlternates.fr}`} />
      )}

      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content="website" />
      <meta property="og:url" content={url} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:locale" content={LOCALE_META[locale].ogLocale} />
      <meta property="og:site_name" content="Yaslan" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />

      {schemas.map((schema, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify({ '@context': 'https://schema.org', ...schema })}
        </script>
      ))}
    </Helmet>
  )
}
